import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import Box from '@mui/material/Box';
import React, { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Outlet } from 'react-router-dom';
import { useInitSentry } from '../../app/hooks/useInitSentry';
import { useCustomer } from '../../services/entities/hooks/useCustomer';
import { useGetUserMeQuery } from '../../services/entities/impl/blfApi/endpoints/users';
import { useInitLaunchDarkly } from '../../services/featureFlags/impl/LaunchDarkly/hooks/useInitLaunchDarkly';
import { CurrServicesAndMeasuresProvider } from '../customerSummary/context/CurrServicesAndMeasuresContext';
import { CustomerSummaryProvider } from '../customerSummary/context/CustomerSummaryContext';
import MapMeasureDialog from '../map/features/measurePopup/MapMeasureDialog';
import { Navbar } from '../navbar/Navbar';
import { RanchControllerProvider } from '../ranches/context/RanchViewController';
import { ScanDataProvider } from '../scanDataApi/context/ScanDataContext';
import InitialSetupDialog from '../settings/general/InitialSetupDialog';
import useHeapAnalytics from '../settings/general/hooks/useHeapAnalytics';
import { useInitSettings } from '../settings/useInitSettings';
import LoadingPlantPage from './LoadingPlantPage';

const PageLayout: React.FC = () => {
  const settingsInitialized = useInitSettings();
  useHeapAnalytics();
  const { data: user } = useGetUserMeQuery();
  const { customer } = useCustomer(user?.customerID);

  // Google Analytics
  ReactGA.initialize('G-Q69T8H28CQ');
  useEffect(() => {
    // TODO: (On Hold) with auth0, only send Login events to GA when user first logs in (not on page refresh)
    if (customer?.name !== undefined && user?.email !== undefined) {
      console.log(
        'GA Login event triggered. Customer name: ',
        customer?.name,
        'User email: ',
        user?.email
      );
      ReactGA.event('Login', {
        // this custom parameter corresponds to the business_name custom dimension in Google Analytics interface
        business_name: customer?.name,
        user_email: user?.email,
      });
    }
  }, [customer?.name, user?.email]);

  // Initialize Sentry with user information
  useInitSentry(user);

  // Initialize LaunchDarkly with user and customer information
  const LdProvider = useInitLaunchDarkly(user, customer);

  if (!settingsInitialized || !LdProvider) {
    return <LoadingPlantPage />;
  }

  return (
    <LdProvider>
      <CustomerSummaryProvider>
        <CurrServicesAndMeasuresProvider>
          <ScanDataProvider>
            <RanchControllerProvider>
              <CanopyProvider>
                <InitialSetupDialog />
                <MapMeasureDialog />
              </CanopyProvider>
              <Box display='flex' height='100%' width='100%'>
                <CanopyProvider>
                  <Navbar />
                </CanopyProvider>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Outlet />
                </Box>
              </Box>
            </RanchControllerProvider>
          </ScanDataProvider>
        </CurrServicesAndMeasuresProvider>
      </CustomerSummaryProvider>
    </LdProvider>
  );
};

export default PageLayout;
